import React, { useState, useEffect } from 'react';
import confetti from 'canvas-confetti';
import { GameHeaderBar } from './GameHeaderBar';

export const CountMatchGame = ({ handleClose, type, onGameComplete }) => {
  const numberRange = Array.from({ length: 10 }, (_, i) => i + 1);
  const [remainingCounts, setRemainingCounts] = useState([...numberRange]);
  const [targetCount, setTargetCount] = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [celebrated, setCelebrated] = useState(false);
  const [animateCard, setAnimateCard] = useState(null);
  const [itemStatus, setItemStatus] = useState({});
  const correctSound = new Audio('/sounds/correct.mp3');
  const errorSound = new Audio('/sounds/error.mp3');
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  // Load saved volume
  useEffect(() => {
    const savedVol = localStorage.getItem('tapGameVolume');
    if (savedVol !== null) {
      setVolume(parseFloat(savedVol));
    }
  }, []);

  useEffect(() => {
    if (remainingCounts.length > 0) {
      selectNextCount(remainingCounts);
    }
  }, []);

  useEffect(() => {
    if (correctCount === 10 && !celebrated) {
      // Calculate game completion stats
      const stars = 3; // Perfect completion
      const score = correctCount * 10; // 10 points per correct match
      
      // Call completion callback before closing
      onGameComplete?.(stars, score);
      
      handleClose?.();
      confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 } });
      setCelebrated(true);
    }
  }, [correctCount, celebrated, onGameComplete]);

  useEffect(() => {
    if (targetCount && !isMuted) {
      const speakTimeout = setTimeout(() => {
        const utter = new SpeechSynthesisUtterance(`Tap the group of ${targetCount} apples`);
        utter.rate = 0.8;
        utter.volume = volume;
        speechSynthesis.speak(utter);
      }, 600);
      return () => clearTimeout(speakTimeout);
    }
  }, [targetCount, isMuted, volume]);

  const selectNextCount = (counts) => {
    let next;
    if (type === 'sequence') {
      next = counts[0];
    } else {
      next = counts[Math.floor(Math.random() * counts.length)];
    }
    setTargetCount(next);
  };

  const handleClick = (num) => {
    if (num === targetCount) {
      if (!isMuted) {
        correctSound.volume = volume;
        correctSound.play();
      }
      setAnimateCard(num);
      setItemStatus(prev => ({ ...prev, [num]: 'correct' }));
      setTimeout(() => setAnimateCard(null), 500);
      const updatedCounts = remainingCounts.filter(n => n !== num);
      setRemainingCounts(updatedCounts);
      setCorrectCount(prev => prev + 1);
      if (updatedCounts.length > 0) {
        selectNextCount(updatedCounts);
      }
    } else {
      if (!isMuted) {
        errorSound.volume = volume;
        errorSound.play();
      }
      setItemStatus(prev => ({ ...prev, [num]: 'wrong' }));
      setTimeout(() => {
        setItemStatus(prev => ({ ...prev, [num]: null }));
      }, 800);
    }
  };

  const renderApples = (count) => {
    return (
      <div className="d-flex flex-wrap justify-content-center" style={{ width: '100%' }}>
        {Array.from({ length: count }).map((_, i) => (
          <img
            key={i}
            src="/image/apple.png"
            alt="apple"
            style={{ width: '32px', height: '32px', margin: '2px' }}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="tap-letter-board-bg">
      <GameHeaderBar
        score={correctCount}
        total={10}
        questionLabel="Tap the group of:"
        question={`${targetCount} Apples`}
        volume={volume}
        onVolumeChange={(v) => {
          setVolume(v);
          localStorage.setItem('tapGameVolume', v);
        }}
        replayTitle="Replay Group"
        onReplay={() => {
          if (targetCount && !isMuted) {
            const utterance = new window.SpeechSynthesisUtterance(`Tap the group of ${targetCount} apples`);
            utterance.rate = 0.8;
            utterance.volume = volume;
            window.speechSynthesis.speak(utterance);
          }
        }}
      />

      <div className="tap-letter-btn-grid">
        {numberRange.map((num) => (
          <button
            key={num}
            onClick={() => handleClick(num)}
            disabled={!remainingCounts.includes(num)}
            className={`btn m-1 letter-btn
              ${animateCard === num ? 'pulse' : ''}
              ${itemStatus[num] === 'correct' ? 'btn-success text-white selected-correct' : ''}
              ${itemStatus[num] === 'wrong' ? 'btn-danger text-white shake' : ''}
              ${!itemStatus[num] ? 'btn-outline-primary' : ''}
            `}
            style={{
              width: 150,
              height: 120,
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column'
            }}
          >
            {renderApples(num)}
          </button>
        ))}
      </div>

      {correctCount === 10 && <h4 className="text-success mt-3">🎉 Great Job! All counts matched!</h4>}

      <style>
        {`
          .tap-letter-board-bg {
            background: url('/image/board.png'), linear-gradient(135deg,rgba(62, 62, 62, 0.3) 0%,rgba(15, 15, 14, 0.41) 100%);
            background-size: cover, cover;
            background-position: center, center;
            border-radius: 32px;
            box-shadow: 0 6px 32px #e0c68a33;
            padding: 32px 12px 24px 12px;
            margin: 0 auto;
            max-width: 900px;
            width: 98%;
            min-height: 400px;
            display: flex;
            flex-direction: column;
            align-items: center;
            position: relative;
            z-index: 2;
            animation: board-pop 0.5s cubic-bezier(.68,-0.55,.27,1.55);
          }
          @keyframes board-pop {
            0% { transform: scale(0.9); opacity: 0; }
            100% { transform: scale(1); opacity: 1; }
          }
          .tap-header-bar {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-radius: 20px;
            box-shadow: 0 2px 8px rgba(224,198,138,0.10);
            padding: 1px 18px 18px 18px;
            margin-bottom: 18px;
            gap: 0;
          }
          .tap-score-box,
          .tap-volume-box {
            display: flex;
            align-items: center;
            border-radius: 14px;
            padding: 6px 14px;
            font-weight: 700;
            font-size: 1.1rem;
            color: #b8860b;
            background: rgba(255, 255, 255, 0.86);
            min-width: 110px;
            box-shadow: 0 1px 4px rgba(224,198,138,0.10);
          }
          .tap-score-label {
            margin-right: 6px;
            font-size: 1rem;
            color: #b8860b;
          }
          .tap-score-value {
            font-size: 1.1rem;
            color: #1976d2;
            margin-left: 4px;
            font-weight: 700;
          }
          .tap-letter-target {
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(224,198,138,0.92);
            border-radius: 18px;
            padding: 8px 32px;
            box-shadow: 0 1px 4px rgba(224,198,138,0.10);
            margin: 0 18px;
            min-width: 180px;
          }
          .tap-letter-label {
            font-size: 1.1rem;
            color: #3e2723;
            margin-right: 8px;
            font-weight: 600;
          }
          .tap-letter-value {
            font-size: 2rem;
            color: #1976d2;
            font-weight: 900;
            letter-spacing: 2px;
            text-shadow: 1px 1px 8px #fff, 0 0 8px #e0c68a;
          }
          .tap-volume-value {
            margin-left: 8px;
            font-size: 1rem;
            color: #1976d2;
            font-weight: 700;
            min-width: 36px;
            display: inline-block;
            text-align: right;
          }
          .tap-letter-btn-grid {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            align-items: center;
            width: 100%;
            min-height: 320px;
            margin-top: 10px;
            gap: 8px;
          }
          .letter-btn {
            padding: 0;
            border-radius: 18px !important;
            background: linear-gradient(135deg,rgb(250, 248, 240) 0%,rgb(248, 228, 166) 100%) !important;
            box-shadow: 0 2px 8px rgba(0,0,0,0.10);
            border: 2px solid rgba(223, 214, 192, 0.65) !important;
            min-width: 110px;
            min-height: 90px;
            width: 110px;
            height: 90px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: transform 0.1s, box-shadow 0.2s;
          }
          .letter-btn:active, .letter-btn:focus {
            transform: scale(0.96);
            box-shadow: 0 0 0 3px #ffe08255;
          }
          .pulse {
            animation: pulse 0.5s;
          }
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.12); }
            100% { transform: scale(1); }
          }
          .shake {
            animation: shake 0.4s;
          }
          @keyframes shake {
            0% { transform: translateX(0); }
            20% { transform: translateX(-8px); }
            40% { transform: translateX(8px); }
            60% { transform: translateX(-8px); }
            80% { transform: translateX(8px); }
            100% { transform: translateX(0); }
          }
          .selected-correct,
          .btn-success.selected-correct {
            background: #000000 !important;
            border-color:rgb(0, 245, 253) !important;
            color: #fff !important;
            box-shadow: 0 0 16px 2px rgba(122, 255, 244, 0.6);
            animation: correct-pop 0.5s;
            transparent: none !important;
          }
          @keyframes correct-pop {
            0% { transform: scale(1.15); }
            100% { transform: scale(1); }
          }
          /* Responsive styles */
          @media (max-width: 1024px) {
            .tap-letter-board-bg {
              max-width: 99vw;
              padding: 18px 2vw 18px 2vw;
              min-height: 260px;
            }
            .tap-letter-btn-grid {
              min-height: 120px;
            }
            .letter-btn {
              min-width: 90px;
              min-height: 70px;
              width: 90px;
              height: 70px;
            }
          }
          @media (max-width: 700px) {
            .tap-header-bar {
              flex-direction: column;
              gap: 10px;
              padding-bottom: 10px;
            }
            .tap-letter-target {
              min-width: 0;
              width: 100%;
              padding: 8px 0;
              margin: 8px 0;
            }
            .tap-letter-board-bg {
              padding: 10px 2vw 10px 2vw;
              min-height: 180px;
            }
            .tap-letter-btn-grid {
              min-height: 80px;
              gap: 4px;
            }
            .letter-btn {
              min-width: 70px;
              min-height: 48px;
              width: 70px;
              height: 48px;
            }
          }
          @media (max-width: 480px) {
            .tap-letter-board-bg {
              padding: 6px 1vw 6px 1vw;
              min-height: 120px;
            }
            .tap-header-bar {
              padding: 6px 4px 6px 4px;
            }
            .tap-letter-btn-grid {
              gap: 2px;
            }
            .letter-btn {
              min-width: 48px;
              min-height: 32px;
              width: 48px;
              height: 32px;
            }
          }
        `}
        </style>
    </div>
  );
};